import React, { useState } from 'react';
import { Modal } from 'antd';
import { Notification } from 'services';
import { COLORS } from 'components/UserDashboardV2/utils/constants';

// Same icon + card treatment as userProfilePicChangeModal.js's "Upload
// profile photo" step (folderImage, #DFDEF5 card).
const folderImage = require('../../Assets/userManagementV3/folder 3.png').default;

// Matches the API's own accepted-type/size limits (see uploadStaffDocument).
const ACCEPTED_EXTENSIONS = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];
export const ACCEPT_ATTR = '.pdf,.doc,.docx,.jpg,.jpeg,.png';
const MAX_SIZE_MB = 10;

// Returns an error message, or null when the file is acceptable. Shared with
// DocumentsTab's direct multi-file picker (ADD DOCUMENT).
export const validateDocumentFile = (picked) => {
    const ext = (picked.name.split('.').pop() || '').toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) return "Only PDF, DOC, DOCX, JPG, JPEG, or PNG files are allowed.";
    if (picked.size / 1024 / 1024 > MAX_SIZE_MB) return "File size must be under 10 MB.";
    return null;
};

// Same rounded-modal convention as userProfilePicChangeModal.js
// (className="modal-round-corner"), just with a file-upload dropzone instead
// of an avatar picker. onUpload(file, callback) is left to the caller —
// this component only handles picking/validating the file and modal state.
// mode="replace" only changes title/button copy — the caller decides which
// API actually gets hit (add vs replace) via what it passes as onUpload.
// Reused for REPLACE instead of a bare hidden <input type="file"> because
// that had no way to detect the OS file picker being cancelled (no file
// chosen never fires a change event), which left the row stuck showing
// "REPLACING..." forever — a modal always has an explicit Cancel/close path.
// Add mode: picking/dropping files closes the modal and hands the valid ones
// to onFilesSelected(files) straight away. Replace mode keeps the
// select-then-REPLACE flow via onUpload.
const AddDocumentModal = ({ visible, onClose, onUpload, onFilesSelected, mode = 'add' }) => {
    const isReplace = mode === 'replace';
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const reset = () => {
        setFile(null);
        setUploading(false);
        setIsDragging(false);
    };

    const handleClose = () => {
        if (uploading) return;
        reset();
        onClose();
    };

    const processFile = (picked) => {
        const errorMsg = validateDocumentFile(picked);
        if (errorMsg) {
            Notification.error("Error", errorMsg);
            return;
        }
        setFile(picked);
    };

    const handleFilesPicked = (picked) => {
        const valid = [];
        const errorMsgs = new Set();
        picked.forEach((f) => {
            const errorMsg = validateDocumentFile(f);
            if (errorMsg) errorMsgs.add(errorMsg);
            else valid.push(f);
        });
        // Nothing usable: show each distinct error once and keep the modal open.
        if (!valid.length) {
            errorMsgs.forEach((msg) => Notification.error("Error", msg));
            return;
        }
        reset();
        onClose();
        // Skipped files are folded into the single summary message after upload.
        onFilesSelected(valid, picked.length - valid.length);
    };

    const handleFileChange = (e) => {
        const picked = Array.from(e.target.files || []);
        e.target.value = null;
        if (!picked.length) return;
        if (isReplace) processFile(picked[0]);
        else handleFilesPicked(picked);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!uploading) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (uploading) return;
        
        const picked = Array.from((e.dataTransfer && e.dataTransfer.files) || []);
        if (!picked.length) return;
        if (isReplace) processFile(picked[0]);
        else handleFilesPicked(picked);
    };

    const handleAdd = () => {
        if (!file || uploading) return;
        setUploading(true);
        onUpload(file, (success) => {
            if (success) {
                reset();
                onClose();
            } else {
                setUploading(false);
            }
        });
    };

    return (
        <Modal
            className="modal-round-corner"
            centered
            visible={visible}
            onCancel={handleClose}
            footer={null}
            width={480}
        >
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 10 }}>
                <div style={{ fontFamily: "Roboto, sans-serif", fontSize: 24, fontWeight: 500, color: "#191919" }}>
                    {isReplace ? "Replace document" : "Add document"}
                </div>

                <label
                    htmlFor="add-document-file-input"
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{
                        marginTop: 30,
                        width: "100%",
                        minHeight: 155,
                        borderRadius: 12,
                        backgroundColor: isDragging ? "#D0CFF0" : "#DFDEF5",
                        border: isDragging ? `2px dashed ${COLORS.primary}` : "2px dashed transparent",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        boxSizing: "border-box",
                        cursor: uploading ? "not-allowed" : "pointer",
                        padding: 16,
                        textAlign: "center",
                        gap: 10,
                        transition: "all 0.2s ease-in-out",
                    }}
                >
                    <img src={folderImage} alt="folder" />
                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: 16, fontWeight: 500, color: "#191919" }}>
                        {isDragging ? "Drop document here" : file ? "Replace document" : "Upload a document"}
                    </span>
                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: 14, color: "#636363" }}>
                        PDF, DOC, DOCX, JPG, JPEG, or PNG · max 10 MB
                    </span>
                    <input
                        id="add-document-file-input"
                        type="file"
                        multiple={!isReplace}
                        accept={ACCEPT_ATTR}
                        disabled={uploading}
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                    />
                </label>

                {file && (
                    <div style={{ width: "100%", marginTop: 12, fontFamily: "Roboto, sans-serif", fontSize: 13, color: "#636363", wordBreak: "break-all" }}>
                        Selected: <span style={{ color: "#191919", fontWeight: 500 }}>{file.name}</span> ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                )}

                {/* Add uploads as soon as files are picked, so only replace needs CANCEL/REPLACE. */}
                {isReplace && (
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16, width: "100%", marginTop: 28 }}>
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={uploading}
                        style={{
                            height: 40,
                            padding: "0 22px",
                            borderRadius: 10,
                            border: `2px solid ${COLORS.primary}`,
                            backgroundColor: "transparent",
                            color: COLORS.primary,
                            fontFamily: "Roboto, sans-serif",
                            fontSize: 14,
                            fontWeight: 700,
                            cursor: uploading ? "not-allowed" : "pointer",
                        }}
                    >
                        CANCEL
                    </button>
                    <button
                        type="button"
                        onClick={handleAdd}
                        disabled={!file || uploading}
                        style={{
                            height: 40,
                            padding: "0 22px",
                            borderRadius: 10,
                            border: "none",
                            backgroundColor: (!file || uploading) ? "#B7A9E8" : COLORS.primary,
                            color: "#FFFFFF",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: 14,
                            fontWeight: 700,
                            cursor: (!file || uploading) ? "not-allowed" : "pointer",
                        }}
                    >
                        {uploading ? "REPLACING..." : "REPLACE"}
                    </button>
                </div>
                )}
            </div>
        </Modal>
    );
};

export default AddDocumentModal;
